import { useState } from "react";
import { ApiRequestError, sendProspectToHerobot, type ProspectDetail } from "../../api";
import { Badge, Btn } from "../../ui";
import { formatDateTime } from "./ProspectListParts";

function isHttpUrl(value: string): boolean {
  return /^https?:\/\//i.test(value);
}

export function HerobotSyncPanel({
  detail,
  onUpdated,
}: {
  detail: ProspectDetail;
  onUpdated?: (next: ProspectDetail) => void;
}) {
  const [note, setNote] = useState("");
  const [busy, setBusy] = useState(false);
  const [message, setMessage] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const sync = detail.herobot;
  const alreadySent = sync.herobotId !== null;

  async function runSync() {
    setBusy(true);
    setMessage(null);
    setError(null);
    try {
      const updated = await sendProspectToHerobot(detail.id, note);
      setNote("");
      setMessage(updated.herobot.message ?? "Sincronizzazione completata");
      onUpdated?.(updated);
    } catch (caught) {
      if (caught instanceof ApiRequestError && caught.status === 409) {
        // Un altro invio è già in volo: non è un errore da segnalare in rosso.
        setMessage("Invio già in corso per questo prospect, attendi il risultato.");
      } else {
        setError(caught instanceof ApiRequestError ? caught.message : "Errore sincronizzazione Herobot");
      }
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="preview-box" style={{ padding: "12px 14px" }}>
      <div className="row gap-8" style={{ alignItems: "center", flexWrap: "wrap" }}>
        <strong>Herobot</strong>
        {!sync.configured && <Badge kind="muted">non configurato</Badge>}
        {sync.configured && !alreadySent && <Badge kind="muted">non inviato</Badge>}
        {sync.configured && alreadySent && !sync.needsUpdate && <Badge kind="info">sincronizzato</Badge>}
        {sync.configured && sync.needsUpdate && <Badge kind="warn">da aggiornare</Badge>}
        {sync.status && <Badge kind="muted">{sync.status}</Badge>}
      </div>

      {!sync.configured && (
        <div className="muted" style={{ marginTop: 8, fontSize: 12 }}>
          L'integrazione non è ancora abilitata per questa organizzazione.
        </div>
      )}

      {sync.configured && (
        <>
          <div className="field" style={{ marginTop: 10 }}>
            <label className="label" htmlFor={`herobot-note-${detail.id}`}>
              Nota per Herobot (facoltativa)
            </label>
            <textarea
              id={`herobot-note-${detail.id}`}
              className="textarea"
              maxLength={2_000}
              placeholder="Indicazioni operative per chi lavorerà il necrologio"
              value={note}
              disabled={busy}
              onChange={(event) => setNote(event.target.value)}
            />
          </div>
          <div className="row gap-8" style={{ marginTop: 10, alignItems: "center", flexWrap: "wrap" }}>
            <Btn
              size="sm"
              variant="primary"
              disabled={busy}
              onClick={() => void runSync()}
            >
              {busy ? "Invio…" : alreadySent ? "Aggiorna in Herobot" : "Salva in Herobot"}
            </Btn>
          </div>
        </>
      )}

      {sync.herobotUrl && isHttpUrl(sync.herobotUrl) && (
        <a
          href={sync.herobotUrl}
          target="_blank"
          rel="noreferrer"
          className="chip mono"
          style={{ display: "inline-flex", marginTop: 10 }}
        >
          apri scheda Herobot ↗
        </a>
      )}

      {sync.lastSyncedAt && (
        <div className="muted" style={{ marginTop: 8, fontSize: 11 }}>
          Ultima sincronizzazione {formatDateTime(sync.lastSyncedAt)}
          {sync.lastSentBy ? ` · ${sync.lastSentBy}` : ""}
        </div>
      )}
      {message && <div style={{ marginTop: 8, color: "var(--accent)", fontSize: 12 }}>{message}</div>}
      {(error ?? sync.error) && (
        <div style={{ marginTop: 8, color: "var(--danger)", fontSize: 12 }}>
          {error ?? sync.error}
        </div>
      )}
    </div>
  );
}
